// src/components/CompareView.js
import React, { useState, useEffect } from 'react';
import { parseModbusData } from '../utils/modbusDecoder';
import { unionParameters, getParamAddress } from '../utils/bmsConstants';
import { extractLiveModbus, extractLiveLocation } from '../utils/telemetrySource';
import { API_BASE } from '../utils/api';
import * as XLSX from 'xlsx';
import LocationMap from './LocationMap';
import './CompareView.css';

function CompareView({ authData, compareSelection, onChangeSelection }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [locations, setLocations] = useState({});
  const [showMap, setShowMap] = useState(true);

  const uniqueDeviceIds = [...new Set(compareSelection.map(s => s.deviceId))];
  const displayParameters = unionParameters([...new Set(compareSelection.map(s => s.type))]);

  const fetchCompareData = async () => {
    if (!authData?.token || compareSelection.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const modbusByDevice = {};
      const nextLocations = { ...locations };

      await Promise.all(uniqueDeviceIds.map(async (deviceId) => {
        const url = `${API_BASE}/api/admin/getDeviceStatus?api_access_token=${authData.token}&deviceId=${deviceId}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        modbusByDevice[deviceId] = extractLiveModbus(data);
        
        const loc = extractLiveLocation(data);
        if (loc && loc.latitude != null && loc.longitude != null) {
          nextLocations[deviceId] = loc;
        }
      }));

      const nextRows = compareSelection.map(selection => {
        const rs485modbus = modbusByDevice[selection.deviceId];
        const parsed = rs485modbus ? parseModbusData(rs485modbus, selection.batterySlaveId, selection.type) : [];
        return {
          key: `${selection.deviceId}_${selection.batterySlaveId}`,
          label: `${selection.deviceLabel} — ${selection.batteryLabel}`,
          type: selection.type,
          params: parsed
        };
      });

      setLocations(nextLocations);
      setRows(nextRows);
      setLastFetched(new Date());
    } catch (err) {
      setError(err.message);
      console.error('Error fetching compare data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompareData(); // Fetch once on mount
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchCompareData, refreshInterval * 1000);
    return () => clearInterval(id);
  }, [autoRefresh, refreshInterval]);

  const formatTime = (date) => {
    if (!date) return 'Never';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
  };

  const getCell = (row, param) => {
    const address = getParamAddress(param.key, row.type) ?? param.address;
    const reg = row.params.find(r => r.address === address);
    if (!reg) return { text: 'N/A', isFault: false };
    if (param.isFault && Array.isArray(reg.parsedValue)) {
      const faults = reg.parsedValue.filter(f => f !== 'No Alarms' && f !== 'No Protection Events');
      return { text: faults.length > 0 ? faults.join(', ') : '', isFault: faults.length > 0 };
    }
    return { text: `${reg.parsedValue} ${reg.unit}`, isFault: false };
  };

  const exportToExcel = () => {
    const worksheetData = rows.map(row => {
      const exportRow = { 'Battery': row.label };
      displayParameters.forEach(param => {
        exportRow[param.name] = getCell(row, param).text;
      });
      return exportRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Compare Batteries');

    const now = new Date();
    const filename = `Compare_Batteries_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const markers = [];
  compareSelection.forEach(item => {
    const loc = locations[item.deviceId];
    if (loc && loc.latitude != null && loc.longitude != null && !(loc.latitude === 0 && loc.longitude === 0)) {
      let marker = markers.find(m => m.deviceId === item.deviceId);
      if (!marker) {
        marker = {
          deviceId: item.deviceId,
          latitude: loc.latitude,
          longitude: loc.longitude,
          label: item.deviceLabel,
          timestamp: loc.timestamp,
          cabinets: []
        };
        markers.push(marker);
      }
      if (!marker.cabinets.includes(item.slaveLabel)) {
        marker.cabinets.push(item.slaveLabel);
      }
    }
  });

  return (
    <div className="historic-data-view">
      <div className="compare-header-row">
        <div className="compare-title-section">
          <h2>Live Battery Status</h2>
          <span className="compare-badge">{compareSelection.length} Cabinets Active</span>
        </div>
        <div className="compare-status-badge">
          <span className="compare-status-dot"></span>
          System Live
        </div>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="compare-toolbar">
        <div className="compare-toolbar-left">
          <button type="button" onClick={onChangeSelection} className="compare-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20v-8M17 20v-4M7 20v-12M2 20V4M22 20v-16"/>
            </svg>
            Change Selection
          </button>
          
          <button type="button" onClick={fetchCompareData} disabled={loading} className="compare-btn primary">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={loading ? { animation: 'spin 1.5s linear infinite' } : undefined}>
              <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
            </svg>
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </button>

          {markers.length > 0 && (
            <button type="button" onClick={() => setShowMap(!showMap)} className={`compare-btn ${showMap ? 'active' : ''}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              {showMap ? 'Hide Map' : 'Show Map'}
            </button>
          )}
        </div>

        <div className="compare-toolbar-right">
          {lastFetched && (
            <span className="compare-last-updated">
              Last updated: {formatTime(lastFetched)}
            </span>
          )}

          <label className="compare-checkbox-label">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="compare-checkbox"
            />
            Auto-refresh
          </label>

          <div className="compare-select-wrapper">
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
              disabled={!autoRefresh}
              className="compare-select"
            >
              <option value={15}>15s interval</option>
              <option value={30}>30s interval</option>
              <option value={60}>60s interval</option>
            </select>
          </div>
        </div>
      </div>

      {showMap && markers.length > 0 && (
        <div className="compare-map-card" style={{ height: '350px' }}>
          <LocationMap markers={markers} />
        </div>
      )}

      {rows.length > 0 && (
        <div className="results">
          <div className="results-header">
            <h3>Compare Data ({rows.length} batteries)</h3>
            <button type="button" onClick={exportToExcel} className="export-button">Export to Excel</button>
          </div>

          <div className="table-container">
            <table className="historic-table">
              <thead>
                <tr>
                  <th>Battery</th>
                  {displayParameters.map(param => (
                    <th key={param.key}>{param.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.key}>
                    <td className="timestamp-cell">{row.label}</td>
                    {displayParameters.map(param => {
                      const cell = getCell(row, param);
                      return (
                        <td key={param.key} style={cell.isFault ? { color: '#ef4444', fontWeight: 600 } : undefined}>
                          {cell.text}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rows.length === 0 && !loading && !error && (
        <div className="no-data">
          <p>Click "Refresh Now" to fetch the latest status for the selected batteries.</p>
        </div>
      )}
    </div>
  );
}

export default CompareView;

